import React, { useEffect, useState } from "react";
import {
  readVipStatusCache,
  writeVipStatusCache,
  withVipStatusRequestLock,
  resolveVipStatusNamespace,
} from "../../../utils/vipStatusCache";

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

export default function MyVipStatus() {
  const [vipData, setVipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadVipStatus = async () => {
      const cacheNamespace = resolveVipStatusNamespace();
      const cached = readVipStatusCache({ namespace: cacheNamespace });

      if (cached) {
        if (isMounted) {
          setVipData(cached);
          setLoading(false);
        }
        return;
      }

      await withVipStatusRequestLock(
        { namespace: cacheNamespace },
        async () => {
          try {
            const response = await fetch(`${BASE_URL}/vip/status`, {
              credentials: "include",
            });
            if (!isMounted) return;

            if (response.ok) {
              const data = await response.json();
              if (!isMounted) return;
              setVipData(data.status);
              writeVipStatusCache({
                value: data.status,
                namespace: cacheNamespace,
              });
            } else if (response.status === 429) {
              const cachedFallback = readVipStatusCache({
                namespace: cacheNamespace,
              });
              if (cachedFallback && isMounted) {
                setVipData(cachedFallback);
              }
            }
          } catch {
            // Keep UI stable on fetch errors
          }
        },
      );

      if (isMounted) setLoading(false);
    };

    loadVipStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading)
    return (
      <div className="min-h-screen cosmic-background flex items-center justify-center">
        <div className="text-center">
          <div className="cosmic-loader mb-4"></div>
          <p className="text-white text-xl font-light animate-pulse">
            Loading VIP Status...
          </p>
        </div>
      </div>
    );

  if (!vipData)
    return (
      <div className="min-h-screen cosmic-background flex items-center justify-center">
        <div className="text-center text-red-300">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-xl font-light">Error loading VIP status</p>
        </div>
      </div>
    );

  const getStatusConfig = () => {
    if (vipData.isVip && vipData.tier) {
      switch (vipData.tier) {
        case "VIP1":
          return {
            title: "Lunar Initiate",
            subtitle: "Your cosmic journey has begun.",
            icon: "✨",
            theme: "lunar",
            cardClass: "lunar-card",
            ringClass: "lunar-ring",
            badgeClass: "lunar-badge",
            statusClass: "lunar-status",
            textGlow: "lunar-text-glow",
          };
        case "VIP2":
          return {
            title: "Pixel Guardian",
            subtitle: "You command the stars.",
            icon: "🌌",
            theme: "guardian",
            cardClass: "guardian-card",
            ringClass: "guardian-ring",
            badgeClass: "guardian-badge",
            statusClass: "guardian-status",
            textGlow: "guardian-text-glow",
          };
        case "VIP3":
          return {
            title: "Celestial Overlord",
            subtitle: "Master of the cosmic realm.",
            icon: "🛡️",
            theme: "overlord",
            cardClass: "overlord-card",
            ringClass: "overlord-ring",
            badgeClass: "overlord-badge",
            statusClass: "overlord-status",
            textGlow: "overlord-text-glow",
          };
        default:
          return {
            title: "VIP Member",
            subtitle: "Welcome to the VIP realm.",
            icon: "🌟",
            theme: "basic",
            cardClass: "basic-card",
            ringClass: "basic-ring",
            badgeClass: "basic-badge",
            statusClass: "basic-status",
            textGlow: "basic-text-glow",
          };
      }
    } else {
      return {
        title: "Standard Member",
        subtitle: "Begin your ascension to VIP status.",
        icon: "👤",
        theme: "standard",
        cardClass: "standard-card",
        ringClass: "standard-ring",
        badgeClass: "standard-badge",
        statusClass: "standard-status",
        textGlow: "standard-text-glow",
      };
    }
  };

  const config = getStatusConfig();

  const ParticleField = ({ theme, isActive }) => (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${isActive ? "opacity-100" : "opacity-60"}`}
    >
      {[
        ...Array(theme === "overlord" ? 20 : theme === "guardian" ? 15 : 10),
      ].map((_, i) => (
        <div
          key={i}
          className={`absolute particle particle-${theme}`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${3 + Math.random() * 4}s`,
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen cosmic-background relative overflow-hidden">
      {/* Cosmic Background Elements */}
      <div className="absolute inset-0 stars-background"></div>
      <div className="absolute inset-0 nebula-overlay"></div>

      <div className="container mx-auto px-8 py-20 relative z-10 max-w-4xl">
        {/* Cosmic Header */}
        <div className="text-center mb-16">
          <h1 className="cosmic-title text-5xl lg:text-6xl font-bold mb-6">
            <span className="cosmic-gradient-text">MY VIP STATUS</span>
          </h1>
          <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto mb-8 leading-relaxed">
            Your current cosmic standing in the PixelMoon universe
          </p>
          <div className="cosmic-divider"></div>
        </div>

        {/* Status Card */}
        <div className="flex justify-center">
          <div
            className={`status-card ${config.cardClass} ${isHovered ? "status-card-active" : ""}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Particle Effects */}
            <ParticleField theme={config.theme} isActive={isHovered} />

            {/* Status Header */}
            <div className="relative z-10 text-center mb-8">
              {/* Avatar Ring */}
              <div
                className={`status-avatar ${config.ringClass} ${isHovered ? "status-avatar-active" : ""}`}
              >
                <div className="status-icon">{config.icon}</div>
                <div className="status-ring-glow"></div>
              </div>

              {/* Badge */}
              <div className={`status-badge ${config.badgeClass}`}>
                {vipData.isVip ? `${config.title} ACTIVE` : "STANDARD MEMBER"}
              </div>
            </div>

            {/* Status Info */}
            <div className="relative z-10 text-center mb-8">
              <h2 className={`status-title ${config.textGlow}`}>
                {config.title}
              </h2>
              <p className="status-subtitle">{config.subtitle}</p>
            </div>

            {/* Status Details */}
            <div className="status-details mb-8">
              <div className="details-grid">
                <div className="detail-item">
                  <div className="detail-label">VIP Status</div>
                  <div className={`detail-value ${config.statusClass}`}>
                    {vipData.isVip ? "ACTIVE" : "INACTIVE"}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Current Tier</div>
                  <div className={`detail-value ${config.statusClass}`}>
                    {typeof vipData.tier === "object"
                      ? vipData.tier?.name
                      : vipData.tier || "None"}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Account Status</div>
                  <div
                    className={`detail-value ${vipData.isBlocked ? "blocked-status" : config.statusClass}`}
                  >
                    {vipData.isBlocked ? "BLOCKED" : "ACTIVE"}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="relative z-10 flex flex-col gap-4">
              {!vipData.isVip && (
                <button className="action-button upgrade-button">
                  <span>🚀 Upgrade to VIP</span>
                  <div className="button-glow"></div>
                </button>
              )}
              <button className="action-button secondary-button">
                <span>📊 View Tier Benefits</span>
                <div className="button-glow"></div>
              </button>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center mt-16">
          <div className="cosmic-divider mb-8"></div>
          <p className="text-lg text-gray-400 font-light">
            {vipData.isVip
              ? "Enjoy your cosmic privileges"
              : "Ready to ascend to VIP status?"}
          </p>
        </div>
      </div>

      <style jsx>{`
        /* Cosmic Background */
        .cosmic-background {
          background: radial-gradient(
            ellipse at top,
            #1a1a2e 0%,
            #16213e 40%,
            #0f0f23 100%
          );
        }

        .stars-background {
          background-image:
            radial-gradient(2px 2px at 20px 30px, #eee, transparent),
            radial-gradient(
              2px 2px at 40px 70px,
              rgba(255, 255, 255, 0.8),
              transparent
            ),
            radial-gradient(1px 1px at 90px 40px, #fff, transparent),
            radial-gradient(
              1px 1px at 130px 80px,
              rgba(255, 255, 255, 0.6),
              transparent
            );
          background-repeat: repeat;
          background-size: 200px 100px;
          animation: twinkle 20s linear infinite;
        }

        .nebula-overlay {
          background:
            radial-gradient(
              ellipse at 20% 50%,
              rgba(120, 69, 255, 0.1) 0%,
              transparent 60%
            ),
            radial-gradient(
              ellipse at 80% 20%,
              rgba(255, 211, 110, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              ellipse at 40% 80%,
              rgba(0, 229, 255, 0.1) 0%,
              transparent 60%
            );
        }

        @keyframes twinkle {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-100px);
          }
        }

        /* Cosmic Title */
        .cosmic-title {
          font-family: "Orbitron", sans-serif;
          animation: cosmicPulse 3s ease-in-out infinite;
        }

        .cosmic-gradient-text {
          background: linear-gradient(
            45deg,
            #fff 0%,
            #00e5ff 25%,
            #7845ff 50%,
            #ffd36e 75%,
            #fff 100%
          );
          background-size: 400% 400%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradientShift 4s ease-in-out infinite;
        }

        @keyframes cosmicPulse {
          0%,
          100% {
            text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
          }
          50% {
            text-shadow:
              0 0 40px rgba(120, 69, 255, 0.5),
              0 0 60px rgba(0, 229, 255, 0.3);
          }
        }

        @keyframes gradientShift {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .cosmic-divider {
          width: 200px;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent,
            #00e5ff,
            #7845ff,
            #ffd36e,
            transparent
          );
          margin: 0 auto;
          animation: dividerGlow 2s ease-in-out infinite;
        }

        @keyframes dividerGlow {
          0%,
          100% {
            box-shadow: 0 0 10px rgba(0, 229, 255, 0.3);
          }
          50% {
            box-shadow: 0 0 20px rgba(120, 69, 255, 0.6);
          }
        }

        /* Cosmic Loader */
        .cosmic-loader {
          width: 80px;
          height: 80px;
          border: 4px solid transparent;
          border-radius: 50%;
          background: conic-gradient(
            from 0deg,
            #00e5ff,
            #7845ff,
            #ffd36e,
            #00e5ff
          );
          animation: cosmicSpin 2s linear infinite;
          margin: 0 auto;
          position: relative;
        }

        .cosmic-loader::before {
          content: "";
          position: absolute;
          inset: 8px;
          background: #0a0a14;
          border-radius: 50%;
        }

        @keyframes cosmicSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Status Card */
        .status-card {
          position: relative;
          padding: 3rem 2.5rem;
          border-radius: 32px;
          backdrop-filter: blur(20px);
          border: 2px solid rgba(255, 255, 255, 0.1);
          transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          overflow: hidden;
          min-height: 600px;
          max-width: 500px;
          width: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          animation: cardAppear 1s ease-out forwards;
        }

        @keyframes cardAppear {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .status-card-active {
          transform: translateY(-15px) scale(1.03);
        }

        /* Card Themes */
        .standard-card {
          background: linear-gradient(
            135deg,
            rgba(100, 100, 100, 0.1) 0%,
            rgba(150, 150, 150, 0.05) 100%
          );
          border-color: rgba(150, 150, 150, 0.2);
        }

        .basic-card {
          background: linear-gradient(
            135deg,
            rgba(255, 215, 0, 0.1) 0%,
            rgba(255, 255, 255, 0.05) 100%
          );
          border-color: rgba(255, 215, 0, 0.2);
        }

        .lunar-card {
          background: linear-gradient(
            135deg,
            rgba(199, 201, 209, 0.12) 0%,
            rgba(248, 249, 255, 0.08) 100%
          );
          border-color: rgba(199, 201, 209, 0.3);
        }

        .guardian-card {
          background: linear-gradient(
            135deg,
            rgba(255, 211, 110, 0.15) 0%,
            rgba(234, 179, 8, 0.1) 100%
          );
          border-color: rgba(255, 211, 110, 0.4);
        }

        .overlord-card {
          background: linear-gradient(
            135deg,
            rgba(10, 10, 20, 0.9) 0%,
            rgba(120, 69, 255, 0.2) 50%,
            rgba(0, 229, 255, 0.15) 100%
          );
          border-color: rgba(0, 229, 255, 0.5);
        }

        /* Avatar Rings */
        .status-avatar {
          position: relative;
          width: 140px;
          height: 140px;
          margin: 0 auto 2rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.4s ease;
        }

        .status-icon {
          font-size: 4rem;
          z-index: 2;
          position: relative;
          filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.3));
        }

        .status-ring-glow {
          position: absolute;
          inset: -8px;
          border-radius: 50%;
          opacity: 0;
          transition: all 0.4s ease;
        }

        .standard-ring {
          border: 3px solid rgba(150, 150, 150, 0.6);
          animation: standardGlow 3s ease-in-out infinite;
        }

        .lunar-ring {
          border: 3px solid rgba(199, 201, 209, 0.6);
          animation: lunarGlow 3s ease-in-out infinite;
        }

        .guardian-ring {
          border: 4px solid;
          border-image: linear-gradient(135deg, #ffd36e, #eab308, #facc15) 1;
          animation: guardianShimmer 2s linear infinite;
        }

        .overlord-ring {
          border: 4px solid transparent;
          background: conic-gradient(
              from 0deg,
              #00e5ff,
              #7845ff,
              #ffd36e,
              #00e5ff
            )
            border-box;
          animation: overlordSpin 4s linear infinite;
        }

        @keyframes standardGlow {
          0%,
          100% {
            box-shadow: 0 0 10px rgba(150, 150, 150, 0.3);
          }
          50% {
            box-shadow: 0 0 20px rgba(150, 150, 150, 0.6);
          }
        }

        @keyframes lunarGlow {
          0%,
          100% {
            box-shadow: 0 0 15px rgba(199, 201, 209, 0.4);
          }
          50% {
            box-shadow: 0 0 30px rgba(199, 201, 209, 0.7);
          }
        }

        @keyframes guardianShimmer {
          0% {
            border-image-source: linear-gradient(90deg, #ffd36e, #eab308);
          }
          100% {
            border-image-source: linear-gradient(90deg, #eab308, #ffd36e);
          }
        }

        @keyframes overlordSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .status-avatar-active .status-ring-glow {
          opacity: 1;
          animation: ringPulse 1.5s ease-in-out infinite;
        }

        @keyframes ringPulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        /* Badges */
        .status-badge {
          position: absolute;
          top: -2rem;
          left: 50%;
          transform: translateX(-50%);
          padding: 0.8rem 2rem;
          border-radius: 30px;
          font-size: 0.85rem;
          font-weight: bold;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          white-space: nowrap;
          animation: badgeFloat 3s ease-in-out infinite;
        }

        @keyframes badgeFloat {
          0%,
          100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-5px);
          }
        }

        .standard-badge {
          background: rgba(150, 150, 150, 0.8);
          color: white;
        }

        .lunar-badge {
          background: rgba(199, 201, 209, 0.9);
          color: #1a1a2e;
        }

        .guardian-badge {
          background: linear-gradient(135deg, #ffd36e, #eab308);
          color: #0a0a14;
        }

        .overlord-badge {
          background: linear-gradient(135deg, #00e5ff, #7845ff);
          color: white;
        }

        /* Typography */
        .status-title {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 1rem;
          font-family: "Orbitron", sans-serif;
          line-height: 1.2;
        }

        .status-subtitle {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.7);
          font-style: italic;
          line-height: 1.5;
          margin-bottom: 2rem;
        }

        .standard-text-glow {
          color: #ccc;
          text-shadow: 0 0 10px rgba(150, 150, 150, 0.3);
        }

        .lunar-text-glow {
          color: #c7c9d1;
          text-shadow: 0 0 15px rgba(199, 201, 209, 0.4);
        }

        .guardian-text-glow {
          color: #ffd36e;
          text-shadow: 0 0 15px rgba(255, 211, 110, 0.5);
        }

        .overlord-text-glow {
          color: #00e5ff;
          text-shadow: 0 0 20px rgba(0, 229, 255, 0.6);
        }

        /* Status Details */
        .status-details {
          position: relative;
          z-index: 2;
          flex-grow: 1;
        }

        .details-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }

        .detail-item {
          background: rgba(0, 0, 0, 0.4);
          padding: 1.5rem;
          border-radius: 20px;
          text-align: center;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .detail-item::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          transition: left 0.5s ease;
        }

        .detail-item:hover::before {
          left: 100%;
        }

        .detail-item:hover {
          transform: translateY(-5px);
          background: rgba(0, 0, 0, 0.5);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .detail-label {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .detail-value {
          font-size: 1.5rem;
          font-weight: bold;
          position: relative;
          z-index: 1;
        }

        .standard-status {
          color: #ccc;
        }

        .lunar-status {
          color: #c7c9d1;
        }

        .guardian-status {
          color: #ffd36e;
        }

        .overlord-status {
          color: #00e5ff;
        }

        .blocked-status {
          color: #ff4444;
          text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
        }

        /* Action Buttons */
        .action-button {
          position: relative;
          width: 100%;
          padding: 1.5rem 2rem;
          border: none;
          border-radius: 25px;
          font-weight: bold;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.4s ease;
          overflow: hidden;
          z-index: 2;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .action-button span {
          position: relative;
          z-index: 2;
          display: block;
        }

        .button-glow {
          position: absolute;
          inset: -2px;
          background: inherit;
          filter: blur(15px);
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: 1;
        }

        .action-button:hover .button-glow {
          opacity: 0.8;
        }

        .upgrade-button {
          background: linear-gradient(135deg, #00e5ff, #7845ff);
          color: white;
          border: 2px solid rgba(0, 229, 255, 0.3);
          margin-bottom: 1rem;
        }

        .upgrade-button:hover {
          box-shadow:
            0 0 40px rgba(0, 229, 255, 0.7),
            0 8px 32px rgba(0, 0, 0, 0.3);
          transform: translateY(-3px);
        }

        .secondary-button {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.2);
        }

        .secondary-button:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-3px);
        }

        /* Particles */
        .particle {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          position: absolute;
          animation: float 4s ease-in-out infinite;
        }

        .particle-standard {
          background: rgba(150, 150, 150, 0.6);
          box-shadow: 0 0 6px rgba(150, 150, 150, 0.4);
        }

        .particle-lunar {
          background: rgba(199, 201, 209, 0.6);
          box-shadow: 0 0 8px rgba(199, 201, 209, 0.4);
        }

        .particle-guardian {
          background: rgba(255, 211, 110, 0.8);
          box-shadow: 0 0 10px rgba(255, 211, 110, 0.6);
        }

        .particle-overlord {
          background: rgba(0, 229, 255, 0.9);
          box-shadow: 0 0 12px rgba(0, 229, 255, 0.7);
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-30px) rotate(180deg);
            opacity: 1;
          }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .cosmic-title {
            font-size: 3.5rem;
          }

          .status-card {
            padding: 2rem 1.5rem;
            margin: 0 1rem;
          }

          .status-avatar {
            width: 120px;
            height: 120px;
          }

          .status-icon {
            font-size: 3rem;
          }

          .status-title {
            font-size: 2rem;
          }

          .status-subtitle {
            font-size: 1rem;
          }

          .detail-item {
            padding: 1.25rem;
          }

          .action-button {
            padding: 1.25rem 1.5rem;
            font-size: 1rem;
          }
        }

        @media (max-width: 480px) {
          .cosmic-title {
            font-size: 2.5rem;
          }

          .status-card {
            padding: 1.5rem 1rem;
            min-height: auto;
          }

          .status-avatar {
            width: 100px;
            height: 100px;
            margin-bottom: 1.5rem;
          }

          .status-icon {
            font-size: 2.5rem;
          }

          .status-badge {
            padding: 0.6rem 1.5rem;
            font-size: 0.75rem;
            top: -1.5rem;
          }

          .status-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </div>
  );
}
